/**
 * AnalysisView — reportes de planograma por piso.
 *
 * Layout vertical split:
 *   ┌─────────────────────────────────────────────────┐
 *   │ HEATMAP (presencia + dwell time)                │
 *   │ Selector: [Sitio ▾] [Piso ▾] [Fecha ▾]          │
 *   │ Plano + capa azul intensa donde la gente pasa   │
 *   │ y/o se queda más tiempo                         │
 *   ├─────────────────────────────────────────────────┤
 *   │ LÍNEAS DE DESPLAZAMIENTO (trazas individuales)  │
 *   │ Selector: [Sitio ▾] [Piso ▾] [Fecha ▾]          │
 *   │ Plano + líneas finas por cada track de SORT     │
 *   │ Densidad acumulada = patrón de circulación      │
 *   └─────────────────────────────────────────────────┘
 *
 * Datos vienen del backend (/api/planogram-reports/*) que agrega los
 * chunks que el edge daemon postea cada 60s. Origen: cono de cámara
 * configurado en el planograma. Acumulativo desde 00:00 hoy por default,
 * con polling cada 5 min. Selector de fecha para días pasados.
 */

import { useState, useEffect, useMemo, useRef } from 'react'
import { useAuthStore } from '../store/authStore'


export default function AnalysisView() {
  const token = useAuthStore(s => s.token)

  // ── Estado compartido (sitio se elige una vez para ambos paneles) ────────
  const [sites,      setSites]      = useState([])
  const [siteId,     setSiteId]     = useState(null)
  const [floors,     setFloors]     = useState([])
  const [activeFloorId, setActiveFloorId] = useState(null)
  const today = new Date().toISOString().slice(0, 10)
  const [date, setDate] = useState(today)

  // ── Carga inicial de sitios ──────────────────────────────────────────────
  useEffect(() => {
    if (!token) return
    // Path con barra final para evitar 307 redirect → 401.
    fetch('/api/sites/?limit=100', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        const list = d.items || d
        if (Array.isArray(list)) {
          setSites(list)
          if (list.length && !siteId) setSiteId(list[0].id)
        }
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  // ── Carga de pisos cuando cambia el sitio ───────────────────────────────
  useEffect(() => {
    if (!siteId || !token) {
      setFloors([])
      setActiveFloorId(null)
      return
    }
    fetch(`/api/site-floor-plans/${siteId}/floors`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        const arr = Array.isArray(list) ? list : []
        setFloors(arr)
        const first = arr.find(f => f.floor_order === 0) || arr[0]
        if (first) setActiveFloorId(first.id)
      })
      .catch(() => setFloors([]))
  }, [siteId, token])

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div style={{
      height: '100%', overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
      background: 'var(--bg-app)',
    }}>
      <div style={{
        padding: '12px 16px', borderBottom: '1px solid var(--border)',
        background: 'var(--bg-surface)',
        display: 'flex', gap: 12, alignItems: 'center',
      }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>Análisis del piso</div>
        <Select
          label="Sitio"
          value={siteId || ''}
          onChange={e => setSiteId(Number(e.target.value))}
          options={sites.map(s => ({ value: s.id, label: s.name }))}
        />
        <Select
          label="Piso"
          value={activeFloorId || ''}
          onChange={e => setActiveFloorId(Number(e.target.value))}
          options={floors.map(f => ({
            value: f.id,
            label: f.floor_label || `Piso ${f.floor_order}`,
          }))}
          disabled={floors.length === 0}
        />
        <input
          type="date"
          value={date}
          max={today}
          onChange={e => setDate(e.target.value)}
          style={{
            padding: '6px 10px', fontSize: 13,
            background: 'var(--bg-elev-1)', color: 'var(--text-primary)',
            border: '1px solid var(--border)', borderRadius: 4,
          }}
        />
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          Acumulado desde 00:00 · auto-refresh 5 min
        </span>
      </div>

      <div style={{
        flex: 1, minHeight: 0,
        display: 'flex', flexDirection: 'column',
      }}>
        <Panel
          title="Mapa de calor — presencia + permanencia"
          subtitle="Áreas más oscuras = más gente o más tiempo de permanencia"
        >
          <HeatmapCanvas
            siteId={siteId}
            floorId={activeFloorId}
            floors={floors}
            date={date}
            token={token}
          />
        </Panel>
        <Panel
          title="Líneas de desplazamiento — circulación"
          subtitle="Cada línea es una trayectoria individual; las zonas con más líneas son rutas frecuentes"
        >
          <TrajectoryCanvas
            siteId={siteId}
            floorId={activeFloorId}
            floors={floors}
            date={date}
            token={token}
          />
        </Panel>
      </div>
    </div>
  )
}


// ─── Componentes helper ────────────────────────────────────────────────────

function Select({ label, value, onChange, options, disabled }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{label}:</span>
      <select
        value={value}
        onChange={onChange}
        disabled={disabled}
        style={{
          padding: '6px 10px', fontSize: 13,
          background: 'var(--bg-elev-1)', color: 'var(--text-primary)',
          border: '1px solid var(--border)', borderRadius: 4,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        {options.length === 0 && <option value="">—</option>}
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  )
}

function Panel({ title, subtitle, children }) {
  return (
    <div style={{
      flex: 1, minHeight: 0,
      display: 'flex', flexDirection: 'column',
      borderBottom: '1px solid var(--border)',
    }}>
      <div style={{
        padding: '8px 16px', display: 'flex', gap: 12, alignItems: 'baseline',
        background: 'var(--bg-elev-1)', borderBottom: '1px solid var(--border)',
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.04em',
          textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
          {title}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{subtitle}</div>
      </div>
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {children}
      </div>
    </div>
  )
}


// ─── HeatmapCanvas ──────────────────────────────────────────────────────────

function HeatmapCanvas({ siteId, floorId, floors, date, token }) {
  const [data, setData] = useState(null)
  const [imgUrl, setImgUrl] = useState(null)
  const [err, setErr] = useState(null)
  const containerRef = useRef(null)

  const floor = floors.find(f => f.id === floorId)

  // Cargar imagen del plano
  useEffect(() => {
    if (!floorId || !token) { setImgUrl(null); return }
    let cancelled = false, created = null
    ;(async () => {
      try {
        const r = await fetch(`/api/site-floor-plans/${floorId}/image`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!r.ok) throw new Error('img')
        const blob = await r.blob()
        if (cancelled) return
        created = URL.createObjectURL(blob)
        setImgUrl(created)
      } catch { if (!cancelled) setImgUrl(null) }
    })()
    return () => { cancelled = true; if (created) URL.revokeObjectURL(created) }
  }, [floorId, token])

  // Cargar heatmap + auto-refresh cada 5 min si la fecha es hoy
  useEffect(() => {
    if (!siteId || !floorId || !token) return
    let cancelled = false
    const today = new Date().toISOString().slice(0, 10)
    const isToday = date === today
    const load = async () => {
      try {
        const r = await fetch(
          `/api/planogram-reports/heatmap?site_id=${siteId}&floor_plan_id=${floorId}&date=${date}`,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const d = await r.json()
        if (!cancelled) { setData(d); setErr(null) }
      } catch (e) {
        if (!cancelled) setErr(e.message)
      }
    }
    load()
    const t = isToday ? setInterval(load, 5 * 60 * 1000) : null
    return () => { cancelled = true; if (t) clearInterval(t) }
  }, [siteId, floorId, date, token])

  if (!floorId) return <EmptyState>Seleccioná un piso</EmptyState>
  if (!imgUrl)  return <EmptyState>Cargando plano…</EmptyState>

  const cells = data?.cells || []
  const maxScore = data?.max_score || 0
  const gridSize = data?.grid_size || 32

  return (
    <div ref={containerRef} style={{
      width: '100%', height: '100%',
      position: 'relative', overflow: 'hidden',
      background: '#1f2937',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {/* Plano */}
      <div style={{
        position: 'relative',
        maxWidth: '100%', maxHeight: '100%',
        aspectRatio: '4 / 3',
      }}>
        <img
          src={imgUrl}
          alt="Plano"
          style={{
            display: 'block',
            width: '100%', height: '100%',
            objectFit: 'contain',
          }}
        />
        {/* Capa de calor SVG */}
        {cells.length > 0 && (
          <svg
            viewBox={`0 0 ${gridSize} ${gridSize}`}
            preserveAspectRatio="none"
            style={{
              position: 'absolute', inset: 0,
              pointerEvents: 'none',
              mixBlendMode: 'multiply',
            }}
          >
            {cells.map((c, i) => {
              const intensity = maxScore > 0 ? c.score / maxScore : 0
              // Azul claro a oscuro, alpha proporcional
              const alpha = Math.min(0.85, 0.15 + intensity * 0.7)
              const hue   = 210                                // azul fijo
              const sat   = 80
              const light = 50 - intensity * 30                // más oscuro = más actividad
              return (
                <rect
                  key={i}
                  x={c.x_bin}
                  y={c.y_bin}
                  width={1.05}
                  height={1.05}
                  fill={`hsla(${hue}, ${sat}%, ${light}%, ${alpha})`}
                />
              )
            })}
          </svg>
        )}
      </div>

      {/* Stats */}
      <div style={{
        position: 'absolute', top: 8, right: 12,
        padding: '4px 10px', borderRadius: 4,
        background: 'rgba(15,23,42,0.85)', color: 'white',
        fontSize: 10,
      }}>
        {cells.length === 0
          ? 'Sin datos para esta fecha'
          : `${data.cameras_with_data} cám · ${cells.length} celdas con actividad`}
        {err && <span style={{ color: '#fbbf24', marginLeft: 8 }}>· {err}</span>}
      </div>

      {/* Leyenda */}
      <div style={{
        position: 'absolute', bottom: 8, right: 12,
        padding: '6px 10px', borderRadius: 4,
        background: 'rgba(15,23,42,0.85)', color: 'white',
        fontSize: 10, display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <span>Bajo</span>
        <div style={{
          width: 80, height: 8, borderRadius: 2,
          background: 'linear-gradient(to right, hsla(210,80%,50%,0.15), hsla(210,80%,20%,0.85))',
        }} />
        <span>Alto</span>
      </div>
    </div>
  )
}


// ─── TrajectoryCanvas ──────────────────────────────────────────────────────

function TrajectoryCanvas({ siteId, floorId, floors, date, token }) {
  const [data, setData] = useState(null)
  const [imgUrl, setImgUrl] = useState(null)
  const [err, setErr] = useState(null)

  // Imagen del plano
  useEffect(() => {
    if (!floorId || !token) { setImgUrl(null); return }
    let cancelled = false, created = null
    ;(async () => {
      try {
        const r = await fetch(`/api/site-floor-plans/${floorId}/image`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!r.ok) throw new Error('img')
        const blob = await r.blob()
        if (cancelled) return
        created = URL.createObjectURL(blob)
        setImgUrl(created)
      } catch { if (!cancelled) setImgUrl(null) }
    })()
    return () => { cancelled = true; if (created) URL.revokeObjectURL(created) }
  }, [floorId, token])

  // Trayectorias + polling
  useEffect(() => {
    if (!siteId || !floorId || !token) return
    let cancelled = false
    const today = new Date().toISOString().slice(0, 10)
    const isToday = date === today
    const load = async () => {
      try {
        const r = await fetch(
          `/api/planogram-reports/trajectories?site_id=${siteId}&floor_plan_id=${floorId}&date=${date}`,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const d = await r.json()
        if (!cancelled) { setData(d); setErr(null) }
      } catch (e) {
        if (!cancelled) setErr(e.message)
      }
    }
    load()
    const t = isToday ? setInterval(load, 5 * 60 * 1000) : null
    return () => { cancelled = true; if (t) clearInterval(t) }
  }, [siteId, floorId, date, token])

  if (!floorId) return <EmptyState>Seleccioná un piso</EmptyState>
  if (!imgUrl)  return <EmptyState>Cargando plano…</EmptyState>

  const paths = data?.paths || []

  return (
    <div style={{
      width: '100%', height: '100%',
      position: 'relative', overflow: 'hidden',
      background: '#1f2937',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        position: 'relative',
        maxWidth: '100%', maxHeight: '100%',
        aspectRatio: '4 / 3',
      }}>
        <img
          src={imgUrl}
          alt="Plano"
          style={{
            display: 'block',
            width: '100%', height: '100%',
            objectFit: 'contain',
          }}
        />
        {paths.length > 0 && (
          <svg
            viewBox="0 0 1 1"
            preserveAspectRatio="none"
            style={{
              position: 'absolute', inset: 0,
              pointerEvents: 'none',
            }}
          >
            {paths.map((p, i) => {
              const pts = p.points || []
              if (pts.length < 2) return null
              const d = pts.map((pt, idx) =>
                `${idx === 0 ? 'M' : 'L'} ${pt.x.toFixed(4)} ${pt.y.toFixed(4)}`,
              ).join(' ')
              return (
                <path
                  key={i}
                  d={d}
                  fill="none"
                  stroke="#38bdf8"
                  strokeWidth={1.5}
                  strokeOpacity={0.55}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              )
            })}
          </svg>
        )}
      </div>

      <div style={{
        position: 'absolute', top: 8, right: 12,
        padding: '4px 10px', borderRadius: 4,
        background: 'rgba(15,23,42,0.85)', color: 'white',
        fontSize: 10,
      }}>
        {paths.length === 0
          ? 'Sin trayectorias para esta fecha'
          : `${paths.length} trazas individuales`}
        {err && <span style={{ color: '#fbbf24', marginLeft: 8 }}>· {err}</span>}
      </div>
    </div>
  )
}


function EmptyState({ children }) {
  return (
    <div style={{
      width: '100%', height: '100%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'var(--text-muted)', fontSize: 13,
    }}>{children}</div>
  )
}
